// src/socket/QuerySocketManager.js
import { io } from "socket.io-client";
import config from "../config/config";

class QuerySocketManager {
  constructor() {
    this.sockets = {}; // key: requestId or `${requestId}_${queryId}`, value: socket
  }

  _getRoomKey(requestId, queryId) {
    return queryId ? `group_${requestId}_${queryId}` : `group_${requestId}`;
  }

  connect(requestId, token, queryId = null, onConnect = null) {
    const key = this._getRoomKey(requestId, queryId);
    if (this.sockets[key]) return this.sockets[key];

    const socket = io(`${config["backend-url"]}/query`, {
      transports: ["websocket"],
    });

    this.sockets[key] = socket;

    socket.on("connect", () => {
      console.log(`✅ Connected to /query for room [${key}]`);
      socket.emit("join_group", { token, request_id: requestId, query_id: queryId });

      if (onConnect) onConnect(socket); // ✅ Call once connected

    });

    socket.on("disconnect", () => {
      console.warn(`⚠️ Disconnected from room [${key}]`);
    });

    socket.on("status", (data) => {
      console.log(`ℹ️ Status for [${key}]:`, data.msg);
    });

    socket.on("error", (err) => {
      console.error(`❌ Socket error in [${key}]:`, err.error || err);
    });

    return socket;
  }

  disconnect(requestId, queryId = null) {
    const key = this._getRoomKey(requestId, queryId);
    if (this.sockets[key]) {
      this.sockets[key].disconnect();
      delete this.sockets[key];
      console.log(`❎ Fully disconnected from room [${key}]`);
    }
  }

  leaveRoom(requestId, token, queryId = null) {
    const key = this._getRoomKey(requestId, queryId);
    if (this.sockets[key]) {
      this.sockets[key].emit("leave_group", { token, request_id: requestId, query_id: queryId });
      console.log(`🚪 Left room [${key}]`);
    }
  }

  createQuery(requestId, subject, description, token, callback) {
    const key = this._getRoomKey(requestId);
    const socket = this.sockets[key];
    if (!socket) return;

    socket.emit("create_query", { request_id: requestId, subject, description, token });
    socket.once("query_created", callback);
  }

  closeQuery(queryId, closed_by_user, token, callback) {
    const socketKeys = Object.keys(this.sockets);
    for (let key of socketKeys) {
      const socket = this.sockets[key];
      if (socket) {
        socket.emit("close_query", { query_id: queryId, closed_by_user, token });
        socket.once("query_closed", callback);
      }
    }
  }

  addComment(queryId, content, token, callback) {
    const socketKeys = Object.keys(this.sockets);
    for (let key of socketKeys) {
      const socket = this.sockets[key];
      if (socket) {
        socket.emit("add_comment", { query_id: queryId, content, token });
        socket.once("comment_added", callback);
      }
    }
  }

  on(event, handler, requestId, queryId = null) {
    const key = this._getRoomKey(requestId, queryId);
    console.log(`🔊 Listening for event [${event}] in room [${key}]`);
    if (this.sockets[key]) {
      this.sockets[key].on(event, handler);
    }
  }

  off(event, requestId, queryId = null) {
    const key = this._getRoomKey(requestId, queryId);
    if (this.sockets[key]) {
      this.sockets[key].off(event);
    }
  }
}

const querySocketManager = new QuerySocketManager();
export default querySocketManager;
